<template>
  <div class="customer-category-page">
    <vx-card v-if="!showCreateFlow" class="mb-6">
      <vs-button color="primary" @click="startNewBatch">+ Nova importação</vs-button>
    </vx-card>

    <vx-card v-if="showCreateFlow" title="Nova importação de categorias" class="mb-6">
      <mass-customer-categories-wizard-progress :current-step="currentStep" />

      <mass-customer-categories-step-identification
        v-if="currentStep === 1"
        v-model="form.name"
      />

      <mass-customer-categories-step-uploads
        v-if="currentStep === 2"
        :tenant-ids="form.tenantIds"
        :selected-tenants-list="selectedTenantsList"
        :uploads="form.uploads"
        @selected-tenants="onTenantsSelected"
        @add-card="addUploadCard"
        @remove-card="removeUploadCard"
        @file-chosen="onFileChosen"
        @sheet-changed="onSheetChanged"
        @update-field="onCardFieldUpdate"
        @set-all-tenants="onCardSetAllTenants"
        @toggle-tenant="onCardToggleTenant"
      />

      <mass-customer-categories-step-review
        v-if="currentStep === 3 && !dryRunBatch"
        :uploads="form.uploads"
        :review-grouped-by-file="reviewGroupedByFile"
        :total-review-items="totalReviewItems"
        :unique-tenants-in-review="uniqueTenantsInReview"
        :loading="loadingDryRun || loadingExecute"
        @confirm-execute="confirmAndExecute"
      />

      <mass-customer-categories-progress-panel
        v-if="currentStep === 3 && dryRunBatch"
        :batch="dryRunBatch"
        :is-auto-refreshing="isAutoRefreshing"
        @retry-failed="retryFailed"
        @finish="finishCreateFlow"
      />

      <div v-if="!dryRunBatch" class="wizard-footer mt-5">
        <vs-button type="border" color="danger" @click="cancelCreateFlow">Cancelar</vs-button>
        <div class="d-flex" style="gap: 10px;">
          <vs-button v-if="currentStep > 1" type="border" color="warning" @click="goToPreviousStep">Voltar</vs-button>
          <vs-button v-if="currentStep < 3" color="primary" :disabled="!!nextStepBlockingReason" @click="goToNextStep">
            Próximo
          </vs-button>
        </div>
      </div>
      <p v-if="!dryRunBatch && currentStep < 3 && nextStepBlockingReason" class="wizard-hint mt-2 mb-0">{{ nextStepBlockingReason }}</p>
    </vx-card>

    <mass-customer-categories-history-section
      :history="history"
      :selected-batch-detail="selectedBatchDetail"
      @show-batch-detail="showBatchDetail"
      @close-batch-detail="selectedBatchDetail = null"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import {mapGetters} from 'vuex';
import {initialFormState, newUploadCard, parseTenantNameFromFqdn} from './helpers';
import MassCustomerCategoriesWizardProgress from './components/MassCustomerCategoriesWizardProgress.vue';
import MassCustomerCategoriesStepIdentification from './components/MassCustomerCategoriesStepIdentification.vue';
import MassCustomerCategoriesStepUploads from './components/MassCustomerCategoriesStepUploads.vue';
import MassCustomerCategoriesStepReview from './components/MassCustomerCategoriesStepReview.vue';
import MassCustomerCategoriesProgressPanel from './components/MassCustomerCategoriesProgressPanel.vue';
import MassCustomerCategoriesHistorySection from './components/MassCustomerCategoriesHistorySection.vue';

export default {
  name: 'MassCustomerCategories',
  components: {
    MassCustomerCategoriesWizardProgress,
    MassCustomerCategoriesStepIdentification,
    MassCustomerCategoriesStepUploads,
    MassCustomerCategoriesStepReview,
    MassCustomerCategoriesProgressPanel,
    MassCustomerCategoriesHistorySection,
  },
  data() {
    return {
      showCreateFlow: false,
      currentStep: 1,
      loadingDryRun: false,
      loadingExecute: false,
      dryRunBatch: null,
      selectedBatchDetail: null,
      autoRefreshTimer: null,
      isAutoRefreshing: false,
      form: initialFormState(),
      finalStatuses: ['completed', 'completed_with_errors', 'failed', 'canceled'],
      columnAutoMap: {
        nameColumn: ['nome', 'nome_cliente', 'name'],
        phoneColumn: ['telefone', 'whatsapp', 'phone', 'celular'],
        emailColumn: ['email', 'e_mail', 'e-mail', 'mail'],
      },
      history: {
        pagination_page: 1,
        pagination_limit: 10,
        total_pages: 0,
        total_items: 0,
        items: [],
      }
    };
  },
  computed: {
    ...mapGetters(['token', 'tenants']),
    selectedTenantsList() {
      const ids = Array.isArray(this.form.tenantIds) ? this.form.tenantIds : [];
      return ids.map((tenantId) => {
        const tenant = Array.isArray(this.tenants) ? this.tenants.find((item) => Number(item.id) === Number(tenantId)) : null;
        const label = tenant ? parseTenantNameFromFqdn(tenant.fqdn) : `tenant_${tenantId}`;
        return { id: Number(tenantId), label };
      });
    },
    totalReviewItems() {
      if (!Array.isArray(this.form.uploads)) return 0;
      return this.form.uploads.reduce((acc, card) => acc + (card.tenantIds || []).length, 0);
    },
    uniqueTenantsInReview() {
      const ids = new Set();
      (this.form.uploads || []).forEach((card) => {
        (card.tenantIds || []).forEach((tenantId) => ids.add(tenantId));
      });
      return ids.size;
    },
    reviewGroupedByFile() {
      return (this.form.uploads || []).map((card) => {
        const sheet = (card.sheets || []).find((item) => item.index === card.sheetIndex) || {};
        const tenants = (card.tenantIds || []).map((tenantId) => {
          const tenant = this.selectedTenantsList.find((item) => item.id === Number(tenantId));
          return { id: Number(tenantId), label: tenant ? tenant.label : `tenant_${tenantId}` };
        });
        return {
          fileName: card.fileName,
          sheetName: sheet.name || '',
          groupName: card.groupName,
          colorTag: card.colorTag,
          totalRows: typeof sheet.total_rows === 'number' ? sheet.total_rows : null,
          tenants,
        };
      });
    },
    nextStepBlockingReason() {
      if (this.currentStep === 1) {
        if (!this.form.name || !this.form.name.trim()) return 'Informe um nome para a importação.';
        return null;
      }

      if (this.currentStep === 2) {
        if (this.selectedTenantsList.length === 0) return 'Selecione pelo menos uma dash.';
        if (!Array.isArray(this.form.uploads) || this.form.uploads.length === 0) return 'Adicione pelo menos uma planilha.';

        const allTenantIdsSelected = new Set();
        for (let cardIndex = 0; cardIndex < this.form.uploads.length; cardIndex += 1) {
          const card = this.form.uploads[cardIndex];
          const position = cardIndex + 1;
          const reason = this.cardBlockingReason(card, position);
          if (reason) return reason;
          card.tenantIds.forEach((tenantId) => allTenantIdsSelected.add(Number(tenantId)));
        }

        const missingTenants = this.selectedTenantsList.filter((tenant) => !allTenantIdsSelected.has(tenant.id));
        if (missingTenants.length > 0) {
          return `Falta atribuir uma planilha para: ${missingTenants.map((tenant) => tenant.label).join(', ')}.`;
        }

        return null;
      }

      return null;
    },
  },
  mounted() {
    this.fetchHistory();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    cardBlockingReason(card, position) {
      if (!card.publicUrl) return `Planilha ${position}: termine o upload do arquivo.`;
      if (!card.nameColumn) return `Planilha ${position}: escolha a coluna do nome.`;
      if (!card.phoneColumn) return `Planilha ${position}: escolha a coluna do telefone.`;
      if (!card.groupName || !card.groupName.trim()) return `Planilha ${position}: informe o nome da categoria.`;
      if (!/^#[0-9a-fA-F]{6}$/.test(card.colorTag || '')) return `Planilha ${position}: cor inválida.`;
      if (!Array.isArray(card.tenantIds) || card.tenantIds.length === 0) return `Planilha ${position}: escolha pelo menos uma dash que receberá esta planilha.`;
      return null;
    },
    startNewBatch() {
      this.form = initialFormState();
      this.dryRunBatch = null;
      this.currentStep = 1;
      this.showCreateFlow = true;
    },
    cancelCreateFlow() {
      this.stopAutoRefresh();
      this.form = initialFormState();
      this.dryRunBatch = null;
      this.currentStep = 1;
      this.showCreateFlow = false;
    },
    finishCreateFlow() {
      this.cancelCreateFlow();
      this.fetchHistory();
    },
    goToNextStep() {
      if (this.nextStepBlockingReason) return;
      if (this.currentStep < 3) this.currentStep += 1;
    },
    goToPreviousStep() {
      if (this.currentStep > 1) this.currentStep -= 1;
    },
    onTenantsSelected(payload) {
      const ids = Array.isArray(payload) ? payload : (payload && payload.tenantIds) || [];
      this.form.tenantIds = ids.map((tenantId) => Number(tenantId)).filter((tenantId) => !Number.isNaN(tenantId));

      const validIds = new Set(this.form.tenantIds);
      (this.form.uploads || []).forEach((card) => {
        card.tenantIds = (card.tenantIds || []).filter((tenantId) => validIds.has(Number(tenantId)));
      });
    },
    addUploadCard() {
      this.form.uploads.push(newUploadCard());
    },
    removeUploadCard(cardIndex) {
      this.form.uploads.splice(cardIndex, 1);
    },
    onCardFieldUpdate({ cardIndex, field, value }) {
      const card = this.form.uploads[cardIndex];
      if (card) this.$set(card, field, value);
    },
    onCardToggleTenant({ cardIndex, tenantId }) {
      const card = this.form.uploads[cardIndex];
      if (!card) return;
      const id = Number(tenantId);
      const has = card.tenantIds.includes(id);
      card.tenantIds = has ? card.tenantIds.filter((existing) => existing !== id) : [...card.tenantIds, id];
    },
    onCardSetAllTenants({ cardIndex, marked }) {
      const card = this.form.uploads[cardIndex];
      if (!card) return;
      card.tenantIds = marked ? this.selectedTenantsList.map((tenant) => tenant.id) : [];
    },
    onSheetChanged({ cardIndex, sheetIndex }) {
      const card = this.form.uploads[cardIndex];
      if (!card) return;
      card.sheetIndex = sheetIndex;
      card.nameColumn = '';
      card.phoneColumn = '';
      card.emailColumn = '';
      this.tryAutoMapColumns(card);
    },
    tryAutoMapColumns(card) {
      const sheet = (card.sheets || []).find((item) => item.index === card.sheetIndex);
      const columns = sheet && Array.isArray(sheet.columns) ? sheet.columns : [];
      const lower = columns.map((column) => `${column}`.toLowerCase().trim());

      const findCol = (candidates) => {
        for (let candidateIndex = 0; candidateIndex < candidates.length; candidateIndex += 1) {
          const matchIndex = lower.indexOf(candidates[candidateIndex]);
          if (matchIndex >= 0) return columns[matchIndex];
        }
        return '';
      };

      Object.keys(this.columnAutoMap).forEach((field) => {
        if (!card[field]) card[field] = findCol(this.columnAutoMap[field]);
      });
    },
    async onFileChosen({ cardIndex, file }) {
      const card = this.form.uploads[cardIndex];
      if (!card || !file) return;

      card.uploading = true;
      card.uploadError = null;
      card.fileName = file.name;

      try {
        const sheets = await this.parseFileLocally(file);
        if (!sheets.length) throw new Error('Não foi possível ler nenhuma aba do arquivo.');

        const referenceTenant = this.selectedTenantsList[0];
        if (!referenceTenant) throw new Error('Selecione pelo menos uma dash antes de enviar arquivos.');

        const response = await this.$store.dispatch('customerCategoryMassUpload', {
          tenantName: referenceTenant.label,
          file,
          sheetsMetadata: sheets,
          token: this.token,
        });

        const data = response && response.data ? response.data : response;
        card.fileId = data.id;
        card.publicUrl = data.public_url;
        card.sheets = sheets;
        card.sheetIndex = sheets[0].index;
        this.tryAutoMapColumns(card);

        this.$vs.notify({ title: 'Arquivo enviado', text: 'Pronto para configurar.', color: 'success' });
      } catch (exception) {
        card.uploadError = this.extractErrorMessage(exception) || 'Falha ao enviar arquivo.';
        card.publicUrl = null;
        card.fileId = null;
        card.sheets = [];
      } finally {
        card.uploading = false;
      }
    },
    parseFileLocally(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
        reader.onload = (loadEvent) => {
          try {
            const data = new Uint8Array(loadEvent.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheets = workbook.SheetNames.map((sheetName, index) => {
              const sheet = workbook.Sheets[sheetName];
              const json = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' });
              const headerRow = (json[0] || []).map((cell) => `${cell}`.trim()).filter((cell) => cell !== '');
              const dataRows = json.slice(1).filter((row) => Array.isArray(row) && row.some((cell) => `${cell}`.trim() !== ''));
              const previewRows = dataRows.slice(0, 10).map((row) => row.map((cell) => `${cell}`));
              return {
                index,
                name: sheetName,
                columns: headerRow,
                preview_rows: previewRows,
                total_rows: dataRows.length,
              };
            });
            resolve(sheets);
          } catch (error) {
            reject(error);
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },
    buildPayload() {
      return {
        name: (this.form.name || '').trim(),
        files: this.form.uploads.map((card) => ({
          file_id: card.fileId,
          sheet_url: card.publicUrl,
          sheet_index: card.sheetIndex,
          sheet_name: ((card.sheets || []).find((sheet) => sheet.index === card.sheetIndex) || {}).name || '',
          group_name: (card.groupName || '').trim(),
          color_tag: card.colorTag,
          name_sheet_column: card.nameColumn,
          phone_sheet_column: card.phoneColumn,
          email_sheet_column: card.emailColumn || '',
          tenants: (card.tenantIds || []).map((tenantId) => {
            const tenant = this.selectedTenantsList.find((item) => item.id === Number(tenantId));
            return { id: Number(tenantId), name: tenant ? tenant.label : `tenant_${tenantId}` };
          }),
        })),
      };
    },
    async confirmAndExecute() {
      this.loadingDryRun = true;
      try {
        const payload = this.buildPayload();
        const dryRunResponse = await this.$store.dispatch('customerCategoryMassDryRun', { payload, token: this.token });
        const dryRunData = dryRunResponse && dryRunResponse.data ? dryRunResponse.data : dryRunResponse;

        this.dryRunBatch = dryRunData;
        this.loadingDryRun = false;

        this.loadingExecute = true;
        const execResponse = await this.$store.dispatch('customerCategoryMassExecute', { batchId: dryRunData.id, token: this.token });
        const execData = execResponse && execResponse.data ? execResponse.data : execResponse;
        this.dryRunBatch = execData;

        if (!this.finalStatuses.includes(execData.status)) this.startAutoRefresh();

        this.$vs.notify({ title: 'Importação enviada', text: 'Acompanhe o andamento abaixo.', color: 'success' });
      } catch (exception) {
        this.$vs.notify({ title: 'Não foi possível enviar', text: this.extractErrorMessage(exception) || 'Tente novamente.', color: 'danger' });
      } finally {
        this.loadingDryRun = false;
        this.loadingExecute = false;
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.isAutoRefreshing = true;
      this.autoRefreshTimer = setInterval(() => {
        this.refreshCurrentBatchSilently();
      }, 5000);
    },
    stopAutoRefresh() {
      this.isAutoRefreshing = false;
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },
    async refreshCurrentBatchSilently() {
      if (!this.dryRunBatch || !this.dryRunBatch.id) return;
      try {
        const response = await this.$store.dispatch('customerCategoryMassHistoryDetail', { batchId: this.dryRunBatch.id, token: this.token });
        const data = response && response.data ? response.data : response;
        this.dryRunBatch = data;
        if (this.finalStatuses.includes(data.status)) {
          this.stopAutoRefresh();
          this.fetchHistory();
        }
      } catch (exception) {
        console.error(exception)
      }
    },
    async retryFailed() {
      if (!this.dryRunBatch || !this.dryRunBatch.id) return;
      try {
        const response = await this.$store.dispatch('customerCategoryMassRetryFailed', { batchId: this.dryRunBatch.id, token: this.token });
        const data = response && response.data ? response.data : response;
        this.dryRunBatch = data;
        if (!this.finalStatuses.includes(data.status)) this.startAutoRefresh();
        this.$vs.notify({ title: 'Reprocessamento iniciado', text: 'Os itens com falha estão sendo enviados novamente.', color: 'success' });
      } catch (exception) {
        this.$vs.notify({ title: 'Falha ao reprocessar', text: this.extractErrorMessage(exception) || 'Tente novamente.', color: 'danger' });
      }
    },
    async showBatchDetail(batchId) {
      const selectedId = Number(this.selectedBatchDetail && this.selectedBatchDetail.id);
      if (!Number.isNaN(selectedId) && selectedId === Number(batchId)) {
        this.selectedBatchDetail = null;
        return;
      }
      try {
        const response = await this.$store.dispatch('customerCategoryMassHistoryDetail', { batchId, token: this.token });
        this.selectedBatchDetail = response && response.data ? response.data : response;
      } catch (exception) {
        this.$vs.notify({ title: 'Não foi possível abrir', text: this.extractErrorMessage(exception) || 'Tente novamente.', color: 'danger' });
      }
    },
    async fetchHistory() {
      try {
        const response = await this.$store.dispatch('customerCategoryMassHistory', {
          page: 1,
          limit: 20,
          status: '',
          requestedBy: '',
          tenantId: '',
          from: null,
          to: null,
          token: this.token,
        });
        this.history = response && response.data ? response.data : response;
      } catch (exception) {
        console.error(exception)
      }
    },
    extractErrorMessage(exception) {
      if (!exception) return null;
      const responseData = exception.response && exception.response.data;
      if (responseData) {
        if (typeof responseData === 'string') return responseData;
        if (responseData.message) return responseData.message;
        if (responseData.errors) {
          const firstKey = Object.keys(responseData.errors)[0];
          if (firstKey && Array.isArray(responseData.errors[firstKey])) {
            return responseData.errors[firstKey][0];
          }
        }
      }
      return exception.message || null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './styles.scss';
.customer-category-page {
  padding-bottom: 24px;
}
</style>
